import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Loader2, RefreshCw, Search } from 'lucide-react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { PmsAutomationTabs, PmsAuditLogSection, type PmsAuditRow } from '@/features/pms-automation'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { ROUTE_BUILDERS } from '@/config/routes'
import {
  fetchMatchingOverview,
  fetchMatchingResidents,
  fetchResidentMatchingAudit,
  saveResidentMatchingAuditNote,
  triggerMatchingRun,
  updateMatchingPropertyMode,
} from '../services/yardiMatchingService'
import { MatchStatusBadge } from '../components/MatchStatusBadge'
import { ModeSelect } from '../components/ModeSelect'
import { SortableHeader } from '../components/SortableHeader'
import { MatchingResidentReviewDialog } from '../components/MatchingResidentReviewDialog'
import { UnmatchableDialog } from '../components/UnmatchableDialog'
import { nextSortState, sortRows, type SortDir } from '../utils/tableSort'
import type {
  MatchingAutomationMode,
  MatchingPhaseSummary,
  MatchingPropertyOverview,
  MatchingResidentRow,
} from '../types'

const SUMMARY_TILES = [
  ['Auto mapped', 'auto_mapped', 'border-emerald-200 bg-emerald-50 text-emerald-800'],
  ['Manual review', 'manual_review', 'border-amber-200 bg-amber-50 text-amber-800'],
  ['Manually mapped', 'manually_mapped', 'border-indigo-200 bg-indigo-50 text-indigo-800'],
  ['Permanent skipped', 'unmatchable', 'border-slate-200 bg-slate-100 text-slate-700'],
  ['Failed', 'failed', 'border-rose-200 bg-rose-50 text-rose-800'],
  ['Pending', 'pending', 'border-sky-200 bg-sky-50 text-sky-800'],
] as const

function SummaryCard({
  title,
  summary,
}: {
  title: string
  summary?: MatchingPhaseSummary
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-slate-800">{title}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {SUMMARY_TILES.map(([label, key, tone]) => (
          <div key={label} className={`rounded-lg border px-3 py-2 ${tone}`}>
            <div className="text-xs opacity-80">{label}</div>
            <div className="text-xl font-semibold">{summary?.[key] ?? 0}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function residentSortValue(row: MatchingResidentRow, field: string): string | number {
  if (field === 'resident') {
    return `${row.first_name || ''} ${row.last_name || ''}`.trim() || `Resident ${row.liberty_entity_id}`
  }
  if (field === 'tcode') return row.matched_tcode || ''
  if (field === 'score') return row.matching_score ?? -1
  if (field === 'status') return row.shadow_decision || row.status || ''
  if (field === 'phase') return row.matching_phase || ''
  return ''
}

export function YardiMatchingPropertyPage() {
  const { propertyId = '' } = useParams()
  const [overview, setOverview] = useState<MatchingPropertyOverview | null>(null)
  const [rows, setRows] = useState<MatchingResidentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [phase, setPhase] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<MatchingResidentRow | null>(null)
  const [reviewRow, setReviewRow] = useState<MatchingResidentRow | null>(null)
  const [running, setRunning] = useState(false)
  const [sortKey, setSortKey] = useState('resident')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [auditRows, setAuditRows] = useState<PmsAuditRow[]>([])

  const load = async () => {
    if (!propertyId) return
    setLoading(true)
    try {
      const [overviewData, residents] = await Promise.all([
        fetchMatchingOverview(propertyId),
        fetchMatchingResidents(propertyId, {
          status: status || undefined,
          phase: phase || undefined,
          search: search || undefined,
        }),
      ])
      setOverview(overviewData)
      setRows(residents)
      void fetchResidentMatchingAudit({ property_id: propertyId })
        .then(setAuditRows)
        .catch(() => setAuditRows([]))
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [propertyId, status, phase])

  const visibleRows = useMemo(
    () => sortRows(rows, (row) => residentSortValue(row, sortKey), sortDir),
    [rows, sortKey, sortDir],
  )

  const changeSort = (field: string) => {
    const next = nextSortState(sortKey, sortDir, field)
    setSortKey(next.key)
    setSortDir(next.dir)
  }

  const changeMode = async (nextMode: MatchingAutomationMode) => {
    if (!overview) return
    try {
      await updateMatchingPropertyMode(overview.yardi_property_id, nextMode)
      setOverview({ ...overview, matching_automation_mode: nextMode })
      showSuccess(`Automation mode set to ${nextMode.toLowerCase()}.`)
      void fetchResidentMatchingAudit({ property_id: propertyId }).then(setAuditRows)
    } catch (error) {
      showApiError(error)
    }
  }

  const runNow = async () => {
    setRunning(true)
    try {
      await triggerMatchingRun({
        property_id: propertyId,
        mode: overview?.matching_automation_mode === 'ENABLED' ? 'ENABLED' : 'SHADOW',
      })
      showSuccess('Matching job queued for this property.')
    } catch (error) {
      showApiError(error)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="min-h-full" data-testid="yardi-matching-property-page">
      <PageHeader title="Resident Matching Results" testIdRoot="yardi-matching-property-header" />
      <div className="space-y-5 p-4 sm:p-6">
        <PmsAutomationTabs />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to="/yardi-matching" className="text-sm font-semibold text-[rgb(21,151,255)]">
            Back to configuration
          </Link>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void load()}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 px-3 text-sm"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button
              type="button"
              disabled={running}
              onClick={() => void runNow()}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-[rgb(21,151,255)] px-3 text-sm font-semibold text-white"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Sync / run now
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{overview?.property_name || 'Property'}</h2>
              <p className="text-sm text-slate-500">
                Yardi code {overview?.yardi_code || '--'} · {overview?.company_name || '--'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                Automation
                <ModeSelect
                  value={overview?.matching_automation_mode || 'DISABLED'}
                  onChange={(nextMode) => void changeMode(nextMode)}
                />
              </label>
              <div className="text-sm text-slate-500">Last run: {overview?.last_run_at || 'Never'}</div>
              {overview?.last_run_id ? (
                <Link
                  to={`/yardi-matching/runs/${overview.last_run_id}`}
                  className="text-sm font-semibold text-[rgb(21,151,255)]"
                >
                  View run details
                </Link>
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SummaryCard title="Current residents" summary={overview?.summary.current} />
          <SummaryCard title="Past residents" summary={overview?.summary.past} />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 md:flex-row md:items-end">
            <label className="min-w-0 flex-1 text-sm font-medium text-slate-700">
              Search
              <div className="relative mt-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void load()
                  }}
                  className="h-10 w-full rounded-md border border-slate-300 pl-9 pr-3"
                  placeholder="Name or TCode"
                />
              </div>
            </label>
            <label className="text-sm font-medium text-slate-700">
              Status
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 md:w-48"
              >
                <option value="">All</option>
                <option value="AUTO_MAPPED">Auto mapped</option>
                <option value="MANUAL_REVIEW">Manual review</option>
                <option value="UNMATCHABLE">Permanent skipped</option>
                <option value="FAILED">Failed</option>
                <option value="PENDING">Pending</option>
                <option value="MANUALLY_MAPPED">Manually mapped</option>
              </select>
            </label>
            <label className="text-sm font-medium text-slate-700">
              Phase
              <select
                value={phase}
                onChange={(event) => setPhase(event.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 md:w-40"
              >
                <option value="">All</option>
                <option value="current">Current</option>
                <option value="past">Past</option>
              </select>
            </label>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">S.No</th>
                  <SortableHeader label="Resident" field="resident" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="TCode" field="tcode" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Score" field="score" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Phase" field="phase" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                      <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                      Loading matching results…
                    </td>
                  </tr>
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                      No matching results yet. Set the property to Shadow and run matching.
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row, index) => {
                    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || `Resident ${row.liberty_entity_id}`
                    const detailTo =
                      row.applicant_id && row.user_id
                        ? ROUTE_BUILDERS.RESIDENT_DETAIL(row.applicant_id, row.user_id)
                        : row.applicant_id
                          ? `/resident?applicant_id=${row.applicant_id}&past_customer=1`
                          : `/resident?q=${encodeURIComponent(row.email || String(row.liberty_entity_id))}`
                    return (
                      <tr key={row.match_status_id} className="border-t border-slate-100">
                        <td className="px-4 py-3 text-slate-500">{index + 1}</td>
                        <td className="px-4 py-3">
                          <Link to={detailTo} className="font-medium text-[rgb(21,151,255)] hover:underline">
                            {name}
                          </Link>
                          <div className="text-xs text-slate-500">{row.email || ''}</div>
                        </td>
                        <td className="px-4 py-3">{row.matched_tcode || '--'}</td>
                        <td className="px-4 py-3">{row.matching_score ?? '--'}</td>
                        <td className="px-4 py-3">
                          <MatchStatusBadge
                            value={row.status === 'UNMATCHABLE' ? row.status : row.shadow_decision || row.status}
                          />
                        </td>
                        <td className="px-4 py-3 capitalize">{row.matching_phase || '--'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="inline-flex h-8 items-center rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                              onClick={() => setReviewRow(row)}
                              data-testid="yardi-matching-review-button"
                            >
                              Review
                            </button>
                            <button
                              type="button"
                              className="inline-flex h-8 items-center rounded-lg bg-[rgb(21,151,255)] px-3 text-xs font-semibold text-white shadow-sm hover:bg-[#0d7acc] disabled:opacity-50"
                              onClick={() => setSelected(row)}
                            >
                              Mark Permanent Skipped
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <PmsAuditLogSection
          title="Audit Logs"
          rows={auditRows}
          testId="pms-resident-property-audit"
          onSave={async (note) => {
            await saveResidentMatchingAuditNote({ note, property_id: propertyId })
            setAuditRows(await fetchResidentMatchingAudit({ property_id: propertyId }))
          }}
        />
      </div>
      <UnmatchableDialog
        key={selected?.match_status_id ?? 'status-closed'}
        row={selected}
        onClose={() => setSelected(null)}
        onSaved={() => void load()}
      />
      <MatchingResidentReviewDialog row={reviewRow} onClose={() => setReviewRow(null)} onSaved={() => void load()} />
    </div>
  )
}
